// Used in avenue-asset-form.component.html

// Angular
import {
    Component,
    AfterViewInit,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    ChangeDetectorRef,
} from "@angular/core";

// RXJS/Store
import { timer, Subject } from "rxjs";
import { takeWhile, map } from "rxjs/operators";

// Services/Types/Helpers
import { TechCheckService } from "#services/tech-check.service";

@Component({
    selector: "avenue-asset-recorder",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./avenue-asset-recorder.component.html",
    styleUrls: ["./avenue-asset-recorder.component.scss"],
    standalone: false
})
export class AvenueAssetRecorderComponent implements AfterViewInit {
    isSecureOrigin: boolean;
    isChromeIpadUser = false;

    errorMsg = "";

    @ViewChild("video") videoElementRef?: ElementRef;
    @ViewChild("studentVideo")
    studentVideoElementRef?: ElementRef;

    @Output() recordedFile = new EventEmitter<Blob>();

    videoElement?: HTMLVideoElement;
    mediaRecorder?: MediaRecorder;
    recordedBlobs: Blob[] = [];
    isRecording = false;
    downloadUrl = "";
    stream: MediaStream | null = null;

    private countdown = new Subject<number>();
    countdownTimer?: number;
    showOverlay = false;

    recordingOpts = {
        audio: {
            echoCancellation: { exact: true },
        },
        video: {
            width: 360,
        },
    };

    constructor(
        private cdr: ChangeDetectorRef,
        private techChecker: TechCheckService
    ) {
        // window.isSecureContext could be used for Chrome
        this.isSecureOrigin =
            location.protocol === "https:" ||
            location.host.includes("localhost");
    }

    async ngAfterViewInit() {
        if (!this.isSecureOrigin) {
            this.errorMsg = "Video Recording is only supported over HTTPS.";
            return;
        } else if (typeof MediaRecorder === "undefined") {
            // MediaRecorder is not supported in Safari and iOS devices :(
            this.errorMsg =
                "Video recording is not supported on this browser. Please use an alternative browser or device.";
            this.isChromeIpadUser = true;
            return;
        }
        if (this.videoElementRef) {
            this.videoElement = this.videoElementRef.nativeElement;
        }
        if (this.videoElement) {
            this.videoElement.muted = true;
        }
        navigator.mediaDevices
            .getUserMedia(this.recordingOpts)
            .then((stream) => {
                this.stream = stream;
                if (this.videoElement) {
                    this.videoElement.srcObject = this.stream;
                }
            })
            .catch((error) => {
                this.errorMsg = "Webcam is unavailable.";
                this.cdr.detectChanges();
            });
    }

    recordingCountdownDelay() {
        if (!this.stream) {
            navigator.mediaDevices
                .getUserMedia(this.recordingOpts)
                .then((stream) => {
                    this.stream = stream;
                    this.startCountdown();
                })
                .catch((error) => {
                    this.errorMsg = "Webcam is unavailable.";
                    this.cdr.detectChanges();
                });
        } else {
            this.startCountdown();
        }
    }

    startCountdown() {
        const startTime = 5;
        this.showOverlay = true;
        timer(0, 1000)
            .pipe(
                takeWhile((t) => t < startTime),
                map((t) => {
                    this.countdownTimer = startTime - t;
                    // Update the page
                    this.cdr.detectChanges();
                    return this.countdownTimer;
                })
            )
            .subscribe(
                (t) => this.countdown.next(t),
                null,
                () => {
                    this.countdown.complete();

                    this.showOverlay = false;
                    // Countdown complete. Start recording.
                    this.startRecording();

                    this.cdr.detectChanges();
                }
            );
    }

    startRecording() {
        if (this.videoElement) {
            this.videoElement.srcObject = this.stream;
            this.videoElement.muted = true;
            this.videoElement.controls = false;
        }

        this.recordedBlobs = [];

        const techInfo = this.techChecker.getDeviceInfo();

        let options;
        const browserVersion = Number.parseInt(techInfo.browserVersion, 10);
        if (techInfo.browser === "Firefox" && browserVersion >= 71) {
            // Firefox v71+ introduces new mime requirements (PV2-2286)
            // https://github.com/mozilla/hubs/issues/2024
            options = { mimeType: "video/webm;codecs=vp8,opus" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
            options = { mimeType: "video/webm; codecs=vp9" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
            options = { mimeType: "video/webm; codecs=vp8" };
        } else if (MediaRecorder.isTypeSupported("video/webm")) {
            options = { mimeType: "video/webm" };
        } else {
            this.errorMsg = "Video Recording is not supported on this browser.";
        }

        try {
            if (this.stream) {
                this.mediaRecorder = new MediaRecorder(this.stream, options);
            }
        } catch (err) {
            // console.log(err);
        }

        this.mediaRecorder?.start(); // collect 100ms of data
        this.isRecording = !this.isRecording;
        this.onDataAvailableEvent();
        this.onStopRecordingEvent();
    }

    stopRecording() {
        this.mediaRecorder?.stop();
        this.isRecording = !this.isRecording;
    }

    playRecording() {
        if (!this.recordedBlobs || !this.recordedBlobs.length) {
            return;
        }

        if (this.videoElement) {
            this.videoElement.src = this.downloadUrl;
            this.videoElement.srcObject = null;

            this.videoElement.controls = true;
            this.videoElement.muted = false;
        }

        this.videoElement?.play();
    }

    onDataAvailableEvent() {
        if (this.mediaRecorder) {
            try {
                this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                    if (event.data && event.data.size > 0) {
                        this.recordedBlobs.push(event.data);
                    }
                };
            } catch (error) {
                // console.log(error);
            }
        }
    }

    onStopRecordingEvent() {
        if (this.mediaRecorder) {
            try {
                this.mediaRecorder.onstop = (event: Event) => {
                    const videoBuffer = new Blob(this.recordedBlobs, {
                        type: "video/webm",
                    });
                    this.downloadUrl = window.URL.createObjectURL(videoBuffer); // you can download with <a> tag

                    this.recordedFile.emit(videoBuffer);

                    if (this.stream) {
                        this.stream
                            .getTracks()
                            .forEach((track) => track.stop());
                        this.stream = null;
                    }

                    // After we stop recording, kick over into play mode.
                    this.playRecording();
                };
            } catch (error) {
                // console.log(error);
            }
        }
    }
}
